import moment from "moment";
import {
  SubmissionOutlineItem,
  SubmissionOutlineStatus,
} from "./SubmissionOutlineNavigation";
import { DocumentMoveDestination } from "./documentMoveValidation";
import { toIncomeSourceTitleCase } from "../../utils/incomeSourceDisplay";


const hasDocumentId = (value: any) => {
  const normalized = String(value ?? "").trim();
  return normalized !== "" && normalized !== "0" && normalized.toLowerCase() !== "null";
};

const getDocumentCount = (value: any): number => {
  if (!value) return 0;
  // Plain array of document records (e.g. record.other_documents).
  if (Array.isArray(value)) {
    return value.filter((item) => hasDocumentId(item?.application_document_id ?? item?.id)).length;
  }
  if (Array.isArray(value?.files)) return value.files.filter((f) => hasDocumentId(f?.application_document_id)).length;
  if (Array.isArray(value?.other_documents)) return value.other_documents.filter((f) => hasDocumentId(f?.application_document_id ?? f?.id)).length;
  if (Number.isFinite(Number(value?.file_count))) return Number(value.file_count);
  let count = 0;
  if (hasDocumentId(value?.application_document_id)) count += 1;
  if (hasDocumentId(value?.BarcodeDocumentID)) count += 1;
  return count;
};

const normalizeIdType = (value: any) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized || normalized === "null" || normalized === "undefined") {
    return null;
  }
  if (normalized === "passport") return 1;
  if (normalized === "license" || normalized === "driver's license" || normalized === "drivers license") return 2;
  if (normalized === "visa") return 11;
  if (
    normalized === "perm id" ||
    normalized === "permanent resident id" ||
    normalized === "permanant residence id"
  ) {
    return 10;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

// Returns a source id only when the source data carries a real identifier.
// Never falls back to a positional index — sending a fabricated source_id to
// automovedocument can create a spurious source on the server.
const getRealSourceId = (...candidates: unknown[]): string | number | null => {
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;
    const normalized = String(candidate).trim();
    if (!normalized || normalized === "0" || normalized.toLowerCase() === "null" || normalized.toLowerCase() === "undefined") {
      continue;
    }
    return candidate as string | number;
  }
  return null;
};

const getOfferLetterCount = (source: any): number => {
  if (!Array.isArray(source?.files)) return 0;
  return source.files.filter((file) => /offer\s*letter/i.test(String(file?.doc_state_type || file?.document_type || file?.file_name || ""))).length;
};

const buildIdTypeTargets = (idSectionData: any): DocumentMoveDestination[] => {
  const frontDocumentId = idSectionData?.application_document_id;
  const backDocumentId = idSectionData?.BarcodeDocumentID;
  const hasIdDocument = hasDocumentId(frontDocumentId) || hasDocumentId(backDocumentId);
  const idType = hasIdDocument
    ? normalizeIdType(idSectionData?.documenttype_id || idSectionData?.IdentificationDocType)
    : null;
  const state = { idType, frontDocumentId, backDocumentId };
  return [
    { id: "id-license-front", kind: "id-type", sectionKey: "id", label: "DL Front", sourceId: 1, documentId: 2, idType: 2, idSlot: "front", currentDocumentCount: getDocumentCount(idSectionData), acceptsDirectDocuments: true, idSlotState: state },
    { id: "id-license-back", kind: "id-type", sectionKey: "id", label: "DL Back", sourceId: 1, documentId: 13, idType: 2, idSlot: "back", currentDocumentCount: getDocumentCount(idSectionData), acceptsDirectDocuments: true, idSlotState: state },
    { id: "id-passport", kind: "id-type", sectionKey: "id", label: "Passport", sourceId: 1, documentId: 1, idType: 1, currentDocumentCount: getDocumentCount(idSectionData), acceptsDirectDocuments: true, idSlotState: state },
    { id: "id-visa", kind: "id-type", sectionKey: "id", label: "Visa", sourceId: 1, documentId: 11, idType: 11, currentDocumentCount: getDocumentCount(idSectionData), acceptsDirectDocuments: true, idSlotState: state },
    { id: "id-permanent-resident", kind: "id-type", sectionKey: "id", label: "Permanent Resident ID", sourceId: 1, documentId: 10, idType: 10, currentDocumentCount: getDocumentCount(idSectionData), acceptsDirectDocuments: true, idSlotState: state },
  ];
};

export const mapSocrStatusToOutlineStatus = (status: any): SubmissionOutlineStatus => {
  const normalized = String(status ?? "").trim().toLowerCase();
  const numeric = Number(status);

  if (numeric === 1 || normalized === "valid") return "Valid";
  if (numeric === 2 || normalized === "unverifiable") return "Unverifiable";
  if (
    numeric === 3 ||
    normalized === "verification required" ||
    normalized === "further documentation required"
  ) {
    return "Verification Required";
  }
  return "NA";
};

export const mapSsnStatusToOutlineStatus = (
  status: any,
  hasUploadedDocument: boolean
): SubmissionOutlineStatus => {
  const normalized = String(status ?? "").trim().toLowerCase();
  const numeric = Number(status);

  if (!hasUploadedDocument) return "NA";
  if (numeric === 1 || normalized === "valid") return "Valid";
  if (numeric === 2 || normalized === "unverifiable") return "Unverifiable";
  if (numeric === 3 || normalized === "no evidence of modification") {
    return "No Evidence of Modification";
  }
  return "NA";
};

export const mapApplicationStatusToOutlineStatus = (
  status: any
): SubmissionOutlineStatus => {
  const normalized = String(status ?? "").trim().toLowerCase();
  const numeric = Number(status);

  if (numeric === 13 || normalized === "verified") return "Verified";
  if (numeric === 15 || normalized === "unverifiable" || normalized === "unverified") {
    return "Unverifiable";
  }
  if (
    numeric === 14 ||
    numeric === 5 ||
    normalized === "verification required" ||
    normalized === "further documentation required" ||
    normalized === "review pending"
  ) {
    return "Verification Required";
  }
  return "NA";
};

export const getApplicationStatusTooltip = (status: any): string | null => {
  const normalized = String(status ?? "").trim().toLowerCase();
  if (normalized === "further documentation required") {
    return "Further Documentation Required";
  }
  return null;
};

export const getSectionAggregateStatus = (
  children: SubmissionOutlineItem[]
): SubmissionOutlineStatus => {
  if (children.length === 0) return "NA";
  if (children.some((item) => item.status === "Unverifiable")) {
    return "Unverifiable";
  }
  if (children.some((item) => item.status === "Verification Required")) {
    return "Verification Required";
  }
  if (children.every((item) => item.status === "Valid")) return "Valid";
  if (children.every((item) => item.status === "Verified")) return "Verified";
  return "NA";
};

export const getSourceNumber = (value: any, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const getSourceDisplayLabel = (
  source: any,
  fallback: string,
  extraCandidates: Array<any> = []
) => {
  const candidates = [
    source?.employer_name,
    source?.employerName,
    source?.name,
    source?.company_name,
    source?.companyName,
    source?.employer,
    ...extraCandidates,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (
        trimmed &&
        trimmed.toLowerCase() !== "null" &&
        trimmed.toLowerCase() !== "undefined"
      ) {
        return toIncomeSourceTitleCase(trimmed);
      }
    }
  }

  return fallback;
};

export const hasNewDocumentFlag = (value: any): boolean => {
  if (!value) return false;
  if (Array.isArray(value)) return value.some((item) => hasNewDocumentFlag(item));
  if (typeof value !== "object") return false;

  return Object.entries(value).some(([key, nestedValue]) => {
    const normalizedKey = key.toLowerCase();
    if (
      normalizedKey.includes("newtag") ||
      normalizedKey === "isnew" ||
      normalizedKey === "newflag" ||
      normalizedKey === "new_flag"
    ) {
      return Boolean(nestedValue);
    }
    return hasNewDocumentFlag(nestedValue);
  });
};

export const parseOutlineTimestamp = (value: any): number | null => {
  if (!value) return null;
  const parsedIso = moment(value, moment.ISO_8601, true);
  if (parsedIso.isValid()) return parsedIso.valueOf();
  const parsedCommon = moment(
    value,
    [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm:ss.SSS",
      "MM/DD/YYYY HH:mm:ss",
      "MM/DD/YYYY h:mm A",
    ],
    true
  );
  if (parsedCommon.isValid()) return parsedCommon.valueOf();
  const nativeValue = new Date(value).getTime();
  return Number.isNaN(nativeValue) ? null : nativeValue;
};

export const hasNewDocumentSinceStatusUpdate = (
  value: any,
  applicationStatusUpdatedOn: string | null | undefined
): boolean => {
  if (!value) return false;
  if (Array.isArray(value)) {
    return value.some((item) =>
      hasNewDocumentSinceStatusUpdate(item, applicationStatusUpdatedOn)
    );
  }
  if (typeof value !== "object") return false;

  const statusUpdatedTime = parseOutlineTimestamp(applicationStatusUpdatedOn);
  return Object.entries(value).some(([key, nestedValue]) => {
    const normalizedKey = key.toLowerCase();
    const looksLikeCreatedAtField =
      normalizedKey === "createdat" ||
      normalizedKey === "created_at" ||
      normalizedKey === "createdon" ||
      normalizedKey === "created_on" ||
      normalizedKey === "file_created_on" ||
      normalizedKey === "backfile_created_on" ||
      normalizedKey === "uploaddate" ||
      normalizedKey === "upload_date";

    if (looksLikeCreatedAtField) {
      const createdTime = parseOutlineTimestamp(nestedValue);
      if (createdTime === null) return false;
      if (statusUpdatedTime === null) return true;
      return createdTime > statusUpdatedTime;
    }

    return hasNewDocumentSinceStatusUpdate(nestedValue, applicationStatusUpdatedOn);
  });
};

export const hasSourceDocuments = (source: any): boolean => {
  if (!source || typeof source !== "object") return false;
  if (Array.isArray(source?.files) && source.files.length > 0) return true;
  if (Number(source?.fileCount) > 0) return true;
  if (Number(source?.document_count) > 0) return true;
  if (Number(source?.doc_count) > 0) return true;
  if (source?.application_document_id) return true;
  if (source?.ApplicationDocumentID) return true;
  if (source?.file || source?.file_image || source?.frontfile || source?.backfile) {
    return true;
  }
  if (source?.path || source?.file_path || source?.filePath) return true;
  return false;
};

const sourceHasNewDocument = (
  source: any,
  applicationStatusUpdatedOn?: string | null
) =>
  hasSourceDocuments(source) &&
  (hasNewDocumentFlag(source) ||
    hasNewDocumentSinceStatusUpdate(source, applicationStatusUpdatedOn));

type BuildSubmissionOutlineItemsArgs = {
  includeProofOfId: boolean;
  includeProofOfSsn: boolean;
  includeEmployment: boolean;
  includeOtherIncome: boolean;
  includePlaidBankIncome: boolean;
  includeBankStatements: boolean;
  includeOtherDocuments: boolean;
  idSectionData: any;
  ssnSectionData: any;
  idSectionSocrStatus: any;
  ssnSectionSocrStatus: any;
  hasSsnSectionDocumentContext: boolean;
  employerSources: any[];
  getEmployerSourceStatus: (sourceIndex: number) => any;
  payrollEmployerNameBySource: Map<number, string>;
  otherIncomeData: any;
  plaidBankIncomeSources: any[];
  plaidBankIncomeValidationLoading: boolean;
  validationCheckForPlaidBankIncome: any;
  bankData: any;
  bankValidationCheck: any;
  record: any;
  applicationStatusUpdatedOn?: string | null;
  // getsectionavailable response: { id, ssn, paystub, bank, other_income, other }.
  // A null/missing section cannot receive moved documents for this applicant —
  // the legacy Move dialog applies the same filter.
  sectionAvailability?: Record<string, unknown> | null;
};

export const buildSubmissionOutlineItems = ({
  includeProofOfId,
  includeProofOfSsn,
  includeEmployment,
  includeOtherIncome,
  includePlaidBankIncome,
  includeBankStatements,
  includeOtherDocuments,
  idSectionData,
  ssnSectionData,
  idSectionSocrStatus,
  ssnSectionSocrStatus,
  hasSsnSectionDocumentContext,
  employerSources,
  getEmployerSourceStatus,
  payrollEmployerNameBySource,
  otherIncomeData,
  plaidBankIncomeSources,
  plaidBankIncomeValidationLoading,
  validationCheckForPlaidBankIncome,
  bankData,
  bankValidationCheck,
  record,
  applicationStatusUpdatedOn,
  sectionAvailability,
}: BuildSubmissionOutlineItemsArgs): SubmissionOutlineItem[] => {
  const items: SubmissionOutlineItem[] = [];
  // Availability gate, mirroring the legacy Move dialog: a section whose
  // getsectionavailable value is null/undefined cannot receive documents.
  // When availability hasn't loaded yet (sectionAvailability undefined),
  // destinations stay enabled to avoid flashing everything disabled.
  const isSectionUnavailable = (sectionKey: string): boolean => {
    // Not loaded yet (undefined) or empty object from a 200 with no data body:
    // don't flag anything unavailable — we don't yet know the real state.
    if (!sectionAvailability || Object.keys(sectionAvailability).length === 0) return false;
    const value = sectionAvailability[sectionKey];
    return value === null || value === undefined;
  };
  const applyAvailability = (
    destination: DocumentMoveDestination,
    availabilityKey: string
  ): DocumentMoveDestination =>
    isSectionUnavailable(availabilityKey)
      ? {
          ...destination,
          acceptsDirectDocuments: false,
          disabledReason:
            "This section is not available as a move destination for this applicant.",
        }
      : destination;
  const idHasNew =
    hasSourceDocuments(idSectionData) &&
    (hasNewDocumentFlag(idSectionData) ||
      hasNewDocumentSinceStatusUpdate(idSectionData, applicationStatusUpdatedOn) ||
      Boolean(record?.NewTagID));
  const ssnHasNew =
    hasSourceDocuments(ssnSectionData) &&
    (hasNewDocumentFlag(ssnSectionData) ||
      hasNewDocumentSinceStatusUpdate(ssnSectionData, applicationStatusUpdatedOn) ||
      Boolean(record?.NewTagID));

  if (includeProofOfId) {
    items.push({
      id: "proof-of-id",
      label: "Proof of Identity",
      status: mapSocrStatusToOutlineStatus(idSectionSocrStatus),
      anchorId: "submission-proof-of-id",
      hasNew: idHasNew,
      moveDestination: { id: "proof-of-id", kind: "section", sectionKey: "id", label: "ID", sourceId: 1, currentDocumentCount: getDocumentCount(idSectionData), acceptsDirectDocuments: false, disabledReason: "Choose a specific ID type (e.g. Front ID, Back ID) below." },
      idTypeTargets: buildIdTypeTargets(idSectionData).map((target) => applyAvailability(target, "id")),
    });
  }

  if (includeProofOfSsn) {
    items.push({
      id: "proof-of-ssn",
      label: "Proof of SSN",
      status: mapSsnStatusToOutlineStatus(
        ssnSectionSocrStatus,
        hasSsnSectionDocumentContext
      ),
      anchorId: "submission-proof-of-ssn",
      hasNew: ssnHasNew,
      moveDestination: applyAvailability({ id: "proof-of-ssn", kind: "section", sectionKey: "ssn", label: "Social Security Number", sourceId: 1, currentDocumentCount: getDocumentCount(ssnSectionData), acceptsDirectDocuments: true }, "ssn"),
    });
  }

  if (includeEmployment) {
    const children: SubmissionOutlineItem[] = employerSources.map((employer, index) => {
      const sourceNumber = getSourceNumber(
        employer?.EmployerNumber ?? employer?.source_id,
        index + 1
      );
      const fallbackLabel = `Source ${sourceNumber}`;
      const plaidEmployerName = payrollEmployerNameBySource.get(sourceNumber);
      const explicitVerificationType = String(employer?.verification_type || "")
        .trim()
        .toLowerCase();
      // "chooser" is a brand-new source where the applicant has not yet picked
      // document upload vs payroll linking — it can receive documents.
      const isPayrollLinked =
        Boolean(plaidEmployerName) || explicitVerificationType === "payroll";
      // Only the database id (or its source_id alias) identifies a move target.
      // EmployerNumber is positional and crashes automovedocument server-side.
      const employerSourceId = getRealSourceId(employer?.id, employer?.source_id);
      const isDocumentVerified = !isPayrollLinked && employerSourceId != null;
      return {
        id: `employment-source-${sourceNumber}`,
        label: getSourceDisplayLabel(employer, fallbackLabel, [
          plaidEmployerName,
        ]),
        status: mapSocrStatusToOutlineStatus(getEmployerSourceStatus(index)),
        anchorId: `employment-source-${sourceNumber}`,
        hasNew: sourceHasNewDocument(employer, applicationStatusUpdatedOn),
        moveDestination: applyAvailability(
          isDocumentVerified
            ? { id: `employment-source-${sourceNumber}`, kind: "source", sectionKey: "paystub", label: fallbackLabel, sourceId: employerSourceId, sourceIndex: index, currentDocumentCount: getDocumentCount(employer), currentOfferLetterCount: getOfferLetterCount(employer), acceptsDirectDocuments: true }
            : { id: `employment-source-${sourceNumber}`, kind: "source", sectionKey: "paystub", label: fallbackLabel, sourceId: employerSourceId, sourceIndex: index, currentDocumentCount: getDocumentCount(employer), acceptsDirectDocuments: false, disabledReason: isPayrollLinked ? "This employer is verified through payroll linking and cannot receive documents." : "This source is not saved yet. Save the employment section (or refresh the page), then try the move again." },
          "paystub"
        ),
      };
    });
    items.push({
      id: "proof-of-employment",
      label: "Employment Income",
      status: getSectionAggregateStatus(children),
      anchorId: "submission-proof-of-employment",
      hasNew: children.length === 0 ? Boolean(record?.NewTagIncome) : false,
      showStatus: false,
      moveDestination: { id: "proof-of-employment", kind: "section", sectionKey: "paystub", label: "Employment Income", acceptsDirectDocuments: false, disabledReason: "Employment Income requires selecting a source." },
      children,
    });
  }

  if (includeOtherIncome) {
    const otherIncomeSources = Array.isArray(
      otherIncomeData?.other_income_details?.otherincome
    )
      ? otherIncomeData?.other_income_details?.otherincome
      : [];
    const children: SubmissionOutlineItem[] = otherIncomeSources.map((source, index) => {
      const sourceNumber = getSourceNumber(source?.other_income_number, index + 1);
      const rawOtherIncomeType =
        typeof source?.other_income_type === "string"
          ? source.other_income_type.trim()
          : "";
      const otherIncomeTypeLabel =
        rawOtherIncomeType &&
        rawOtherIncomeType.toLowerCase() !== "null" &&
        rawOtherIncomeType.toLowerCase() !== "undefined"
          ? rawOtherIncomeType
          : "";
      const otherIncomeSourceId = getRealSourceId(source?.other_income_number, source?.source_id);
      return {
        id: `other-income-source-${sourceNumber}`,
        label: otherIncomeTypeLabel || `Source ${sourceNumber}`,
        status: "NA" as SubmissionOutlineStatus,
        anchorId: `other-income-source-${sourceNumber}`,
        hasNew: sourceHasNewDocument(source, applicationStatusUpdatedOn),
        moveDestination: applyAvailability(
          otherIncomeSourceId != null
            ? { id: `other-income-source-${sourceNumber}`, kind: "source", sectionKey: "other_income", label: otherIncomeTypeLabel || `Source ${sourceNumber}`, sourceId: otherIncomeSourceId, sourceIndex: index, currentDocumentCount: getDocumentCount(source), acceptsDirectDocuments: true }
            : { id: `other-income-source-${sourceNumber}`, kind: "source", sectionKey: "other_income", label: otherIncomeTypeLabel || `Source ${sourceNumber}`, sourceId: null, sourceIndex: index, currentDocumentCount: getDocumentCount(source), acceptsDirectDocuments: false, disabledReason: "This source is missing an identifier and cannot receive documents." },
          "other_income"
        ),
      };
    });
    items.push({
      id: "proof-of-other-income",
      label: "Other Income",
      status: getSectionAggregateStatus(children),
      anchorId: "submission-proof-of-other-income",
      hasNew:
        children.length === 0 &&
        sourceHasNewDocument(otherIncomeData?.other_income_details, applicationStatusUpdatedOn),
      showStatus: false,
      moveDestination: { id: "proof-of-other-income", kind: "section", sectionKey: "other_income", label: "Other Income", acceptsDirectDocuments: false, disabledReason: "Other Income requires selecting a source." },
      children,
    });
  }

  if (includePlaidBankIncome) {
    const hasPlaidLinkedBankIncomeSources = plaidBankIncomeSources.length > 0;
    items.push({
      id: "plaid-bank-income",
      label: "Income from Bank Linking",
      status: plaidBankIncomeValidationLoading
        ? "NA"
        : validationCheckForPlaidBankIncome?.validation_status === "valid"
          ? "Valid"
          : "NA",
      anchorId: "plaid-bank-income-anchor",
      disableDuringMove: true,
      hasNew:
        hasPlaidLinkedBankIncomeSources &&
        (plaidBankIncomeSources.some((source) =>
          sourceHasNewDocument(source, applicationStatusUpdatedOn)
        ) ||
          Boolean(record?.NewTagBankIncome)),
    });
  }

  if (includeBankStatements) {
    const bankSources = Array.isArray(bankData?.banking_details?.banking)
      ? bankData?.banking_details?.banking
      : [];
    const children: SubmissionOutlineItem[] = bankSources.map((source, index) => {
      const sourceNumber = getSourceNumber(
        source?.bank_statement_number ?? source?.source_id,
        index + 1
      );
      const status =
        bankData?.banking_details?.SOCRstatus?.[`Source_${sourceNumber}`] ||
        bankValidationCheck?.other_check?.[`source_${sourceNumber}`]?.SOCRStatus;
      return {
        id: `bank-source-${sourceNumber}`,
        label: getSourceDisplayLabel({}, `Bank Source ${sourceNumber}`, [
          source?.bank_name,
          source?.bankName,
          source?.BankName,
          source?.account_name,
          source?.accountName,
          source?.bank?.name,
          source?.name,
          source?.company_name,
          source?.companyName,
        ]),
        status: mapSocrStatusToOutlineStatus(status),
        anchorId: `bank-source-${sourceNumber}`,
        hasNew: sourceHasNewDocument(source, applicationStatusUpdatedOn),
        moveDestination: (() => {
          const bankSourceId = getRealSourceId(source?.bank_statement_number, source?.source_id);
          return applyAvailability(
            bankSourceId != null
              ? { id: `bank-source-${sourceNumber}`, kind: "source" as const, sectionKey: "bank" as const, label: `Bank Source ${sourceNumber}`, sourceId: bankSourceId, sourceIndex: index, currentDocumentCount: getDocumentCount(source), acceptsDirectDocuments: true }
              : { id: `bank-source-${sourceNumber}`, kind: "source" as const, sectionKey: "bank" as const, label: `Bank Source ${sourceNumber}`, sourceId: null, sourceIndex: index, currentDocumentCount: getDocumentCount(source), acceptsDirectDocuments: false, disabledReason: "This source is missing an identifier and cannot receive documents." },
            "bank"
          );
        })(),
      };
    });
    items.push({
      id: "bank-statements",
      label: "Banking",
      status: getSectionAggregateStatus(children),
      anchorId: "submission-bank-statements",
      hasNew: children.length === 0 ? Boolean(record?.NewTagBanking) : false,
      showStatus: false,
      moveDestination: { id: "bank-statements", kind: "section", sectionKey: "bank", label: "Banking", acceptsDirectDocuments: false, disabledReason: "Banking requires selecting a source." },
      children,
    });
  }

  if (includeOtherDocuments) {
    items.push({
      id: "other-documents",
      label: "Other Documents",
      status: "NA",
      anchorId: "submission-other-documents",
      hasNew: Boolean(record?.NewTagOther),
      showStatus: false,
      moveDestination: applyAvailability({ id: "other-documents", kind: "section", sectionKey: "other", label: "Other Documents", sourceId: 1, currentDocumentCount: getDocumentCount(record?.other_documents ?? record?.other_documents_details ?? null), acceptsDirectDocuments: true }, "other"),
    });
  }

  return items;
};
